<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useCallsStore } from 'dashboard/stores/calls';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import WhatsappCallsAPI from 'dashboard/api/channel/whatsapp/whatsappCallsAPI';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const route = useRoute();
const { t } = useI18n();
const store = useStore();
const callsStore = useCallsStore();
const inboxes = ref([]);
const agentsByInbox = ref({});
const monitoredCalls = ref([]);
const recentCalls = ref([]);
const callMetrics = ref(null);
const rerouteTargets = ref({});
const reroutingCallId = ref(null);
const transferTargets = ref({});
const transferCustomerRequested = ref({});
const transferringCallId = ref(null);
const terminationDialog = ref(null);
const selectedCallToTerminate = ref(null);
const isTerminatingCall = ref(false);
const operationsView = ref('agents');
const loading = ref(true);
const refreshing = ref(false);
let refreshTimer;
let callsRefreshTimer;

const voiceInboxes = computed(() =>
  inboxes.value.filter(
    inbox => inbox.channel_type === 'Channel::Whatsapp' && inbox.voice_enabled
  )
);
const teams = computed(() => store.getters['teams/getTeams'] || []);
const canManageCallSettings = computed(() => {
  const account = store.getters.getCurrentUser?.accounts?.find(
    item => Number(item.id) === Number(route.params.accountId)
  );
  return (
    account?.role === 'administrator' ||
    (account?.permissions || []).includes('inbox_manage')
  );
});

const activeWhatsappCalls = computed(() =>
  callsStore.calls.filter(call => call.provider === 'whatsapp')
);

const eligibleAgents = inbox => {
  const agents = agentsByInbox.value[inbox.id] || [];
  const selectedIds = (inbox.call_queue_config?.agent_ids || []).map(Number);
  return selectedIds.length
    ? agents.filter(agent => selectedIds.includes(Number(agent.id)))
    : agents;
};

const allAgents = computed(() => {
  const unique = new Map();
  voiceInboxes.value.forEach(inbox => {
    eligibleAgents(inbox).forEach(agent => unique.set(Number(agent.id), agent));
  });
  return [...unique.values()];
});

const onlineAgents = computed(
  () =>
    allAgents.value.filter(agent => agent.availability_status === 'online')
      .length
);
const busyAgents = computed(
  () =>
    allAgents.value.filter(agent => agent.availability_status === 'busy').length
);
const receivingInboxes = computed(
  () => voiceInboxes.value.filter(inbox => inbox.inbound_calls_enabled).length
);
const queuedCalls = computed(() =>
  monitoredCalls.value.filter(
    call =>
      call.state === 'queued' ||
      call.state === 'connecting' ||
      call.state === 'transferring'
  )
);
const aiCalls = computed(() =>
  monitoredCalls.value.filter(call => call.state === 'lumerai')
);
const humanCalls = computed(() =>
  monitoredCalls.value.filter(call => call.state === 'agent')
);
const staleCalls = computed(() =>
  monitoredCalls.value.filter(call => call.state === 'stale')
);
const liveMonitoredCalls = computed(() =>
  monitoredCalls.value.filter(call => call.state !== 'stale')
);

const refreshMonitoredCalls = async () => {
  try {
    const {
      calls,
      recent_calls: history,
      metrics,
    } = await WhatsappCallsAPI.queueStatus();
    monitoredCalls.value = calls || [];
    recentCalls.value = history || [];
    callMetrics.value = metrics || null;
  } catch {
    // Keep the last successful snapshot visible during brief network/API blips.
  }
};

const requestForcedTermination = call => {
  selectedCallToTerminate.value = call;
  terminationDialog.value?.open();
};

const confirmForcedTermination = async () => {
  const call = selectedCallToTerminate.value;
  if (!call) return;

  isTerminatingCall.value = true;
  try {
    await WhatsappCallsAPI.terminate(call.id);
    useAlert(t('VOICE.CENTER.TERMINATION_REQUESTED'));
    terminationDialog.value?.close();
    selectedCallToTerminate.value = null;
    await refreshMonitoredCalls();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('VOICE.CENTER.TERMINATION_ERROR')
    );
  } finally {
    isTerminatingCall.value = false;
  }
};

const rerouteCall = async call => {
  const [targetType, targetId] = (
    rerouteTargets.value[call.id] || 'queue'
  ).split(':');
  reroutingCallId.value = call.id;
  try {
    const result = await WhatsappCallsAPI.rerouteHandoff(
      call.id,
      targetType,
      targetId
    );
    useAlert(
      result.status === 'returned_to_ai'
        ? t('VOICE.CENTER.RETURNED_TO_AI')
        : t('VOICE.CENTER.REROUTED', { destination: result.destination })
    );
    await refreshMonitoredCalls();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('VOICE.CENTER.REROUTE_ERROR'));
  } finally {
    reroutingCallId.value = null;
  }
};

const transferCall = async call => {
  const [targetType, targetId] = (transferTargets.value[call.id] || '').split(
    ':'
  );
  if (!targetType || !transferCustomerRequested.value[call.id]) return;

  transferringCallId.value = call.id;
  try {
    const result = await WhatsappCallsAPI.transferHandoff(
      call.id,
      targetType,
      targetId,
      true
    );
    useAlert(
      t('VOICE.CENTER.TRANSFER_STARTED', { destination: result.destination })
    );
    await refreshMonitoredCalls();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('VOICE.CENTER.TRANSFER_ERROR'));
  } finally {
    transferringCallId.value = null;
    transferCustomerRequested.value[call.id] = false;
  }
};

const resetTransferCustomerRequest = callId => {
  transferCustomerRequested.value[callId] = false;
};

const load = async (quiet = false) => {
  if (quiet) refreshing.value = true;
  else loading.value = true;

  try {
    await store.dispatch('teams/get').catch(() => {});
    const { data } = await InboxesAPI.get();
    const availableInboxes = (data.payload || []).filter(
      inbox => inbox.channel_type === 'Channel::Whatsapp' && inbox.voice_enabled
    );
    inboxes.value = availableInboxes;

    const agentResults = await Promise.all(
      availableInboxes.map(async inbox => {
        try {
          const response = await InboxesAPI.getAssignableAgents(inbox.id);
          return [inbox.id, response.data.payload || []];
        } catch {
          return [inbox.id, []];
        }
      })
    );
    agentsByInbox.value = Object.fromEntries(agentResults);
  } catch {
    if (!quiet) useAlert(t('VOICE.ALERTS.CENTER_LOAD_ERROR'));
  } finally {
    loading.value = false;
    refreshing.value = false;
  }
};

const agentStatusLabel = status => {
  if (status === 'online') return t('VOICE.CENTER.AGENT_ONLINE');
  if (status === 'busy') return t('VOICE.CENTER.AGENT_BUSY');
  return t('VOICE.CENTER.AGENT_OFFLINE');
};

const callDestinationLabel = call =>
  call.destination ||
  (call.direction === 'outgoing'
    ? t('VOICE.CENTER.OUTGOING_CALL')
    : t('VOICE.CENTER.GENERAL_QUEUE'));

const agentStatusClass = status => {
  if (status === 'online') return 'bg-n-teal-3 text-n-teal-11';
  if (status === 'busy') return 'bg-n-amber-3 text-n-amber-11';
  return 'bg-n-alpha-2 text-n-slate-10';
};

const callStateLabel = call =>
  call.isActive ? t('VOICE.CENTER.IN_PROGRESS') : t('VOICE.CENTER.RINGING');

const inboxCalls = inboxId =>
  monitoredCalls.value.filter(
    call => Number(call.inbox_id) === Number(inboxId)
  );

const inboxCallCount = (inboxId, states) =>
  inboxCalls(inboxId).filter(call => states.includes(call.state)).length;

const monitoredCallLabel = call => {
  if (call.state === 'transferring')
    return t('VOICE.CENTER.TRANSFERRING', {
      destination: call.destination || t('VOICE.CENTER.GENERAL_QUEUE'),
    });
  if (call.state === 'queued') return t('VOICE.CENTER.WAITING_FOR_AGENT');
  if (call.state === 'connecting') return t('VOICE.CENTER.AGENT_CONNECTING');
  if (call.state === 'agent')
    return t('VOICE.CENTER.WITH_AGENT', {
      agent: call.agent_name || t('VOICE.CENTER.AGENT'),
    });
  if (call.state === 'lumerai') return t('VOICE.CENTER.WITH_LUMERAI');
  if (call.state === 'stale') return t('VOICE.CENTER.SYNC_PENDING');
  if (call.status === 'ringing') return t('VOICE.CENTER.RINGING');
  return call.status || t('VOICE.CENTER.IN_PROGRESS');
};

const callStatusLabel = status => {
  const labels = {
    ringing: t('VOICE.CENTER.STATUS_RINGING'),
    in_progress: t('VOICE.CENTER.STATUS_ANSWERED'),
    completed: t('VOICE.CENTER.STATUS_COMPLETED'),
    no_answer: t('VOICE.CENTER.STATUS_NO_ANSWER'),
    failed: t('VOICE.CENTER.STATUS_FAILED'),
    rejected: t('VOICE.CENTER.STATUS_REJECTED'),
  };
  return labels[status] || status?.replaceAll('_', ' ') || '—';
};

const routingStageLabel = call => {
  if (call.routing_stage === 'overflow')
    return t('VOICE.CENTER.OVERFLOW', {
      index:
        call.overflow_index == null
          ? ''
          : ` #${Number(call.overflow_index) + 1}`,
    });
  if (call.routing_stage === 'direct') return t('VOICE.CENTER.DIRECT_AGENT');
  if (call.routing_stage === 'ring_all') return t('VOICE.STRATEGIES.RING_ALL');
  if (call.routing_stage === 'round_robin')
    return t('VOICE.STRATEGIES.ROUND_ROBIN');
  return call.routing_stage?.replaceAll('_', ' ');
};

const lifecycleEventLabel = event => {
  const labels = {
    incoming_received: t('VOICE.CENTER.EVENT_INCOMING'),
    human_answered: t('VOICE.CENTER.EVENT_HUMAN_ANSWERED'),
    call_answered: t('VOICE.CENTER.EVENT_CALL_ANSWERED'),
    handoff_prepared: t('VOICE.CENTER.EVENT_HANDOFF_PREPARED'),
    handoff_queued: t('VOICE.CENTER.EVENT_HANDOFF_QUEUED'),
    handoff_routed_to_agent: t('VOICE.CENTER.EVENT_ROUTED_AGENT'),
    handoff_routed_to_team: t('VOICE.CENTER.EVENT_ROUTED_TEAM'),
    handoff_manually_rerouted: t('VOICE.CENTER.EVENT_MANUALLY_REROUTED'),
    agent_transfer_started: t('VOICE.CENTER.EVENT_AGENT_TRANSFER_STARTED'),
    agent_transfer_completed: t('VOICE.CENTER.EVENT_AGENT_TRANSFER_COMPLETED'),
    agent_transfer_returned_to_current_agent: t(
      'VOICE.CENTER.EVENT_AGENT_TRANSFER_RETURNED'
    ),
    agents_alerted: t('VOICE.CENTER.EVENT_AGENTS_ALERTED'),
    queue_overflow: t('VOICE.CENTER.EVENT_QUEUE_OVERFLOW'),
    agent_answered_handoff: t('VOICE.CENTER.EVENT_HANDOFF_ANSWERED'),
    handoff_returned_to_ai: t('VOICE.CENTER.EVENT_RETURNED_TO_AI'),
    call_rejected: t('VOICE.CENTER.EVENT_CALL_REJECTED'),
    call_timed_out: t('VOICE.CENTER.EVENT_CALL_TIMEOUT'),
    call_ended: t('VOICE.CENTER.EVENT_CALL_ENDED'),
  };
  return labels[event] || event?.replaceAll('_', ' ');
};

const averageFirstAudioLatency = quality => {
  const samples = quality?.first_audio_latency_ms || [];
  if (!samples.length) return null;
  return Math.round(
    samples.reduce((sum, value) => sum + Number(value || 0), 0) / samples.length
  );
};

const monitoredCallTone = state => {
  if (state === 'stale') return 'bg-n-amber-3 text-n-amber-11';
  if (state === 'queued' || state === 'connecting' || state === 'transferring')
    return 'bg-n-blue-3 text-n-blue-11';
  if (state === 'agent') return 'bg-n-teal-3 text-n-teal-11';
  return 'bg-n-alpha-2 text-n-slate-11';
};

onMounted(() => {
  load();
  refreshMonitoredCalls();
  refreshTimer = window.setInterval(() => load(true), 30_000);
  callsRefreshTimer = window.setInterval(refreshMonitoredCalls, 5_000);
});

onUnmounted(() => {
  window.clearInterval(refreshTimer);
  window.clearInterval(callsRefreshTimer);
});
</script>

<template>
  <main class="flex-1 min-w-0 overflow-y-auto bg-n-background">
    <div class="w-full px-6 py-8 2xl:px-10">
      <header class="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div class="flex items-center gap-4">
          <div
            class="grid place-items-center size-12 rounded-xl bg-n-brand/10 text-n-brand"
          >
            <i class="i-lucide-headset text-2xl" />
          </div>
          <div>
            <h1 class="text-2xl font-semibold text-n-slate-12">
              {{ t('VOICE.CENTER.TITLE') }}
            </h1>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('VOICE.CENTER.SUBTITLE') }}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Button
            :label="t('VOICE.CENTER.REFRESH')"
            icon="i-lucide-refresh-cw"
            variant="outline"
            :disabled="refreshing || loading"
            @click="load()"
          />
          <RouterLink
            v-if="canManageCallSettings"
            :to="{
              name: 'voice_queues_settings',
              params: { accountId: route.params.accountId },
            }"
            class="inline-flex"
          >
            <Button
              :label="t('VOICE.CENTER.SETTINGS')"
              icon="i-lucide-settings-2"
            />
          </RouterLink>
        </div>
      </header>

      <div v-if="loading" class="grid place-items-center min-h-64">
        <Spinner />
      </div>

      <template v-else>
        <section
          class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-7"
        >
          <article class="rounded-xl border border-n-weak bg-n-surface-1 p-5">
            <div class="flex items-center justify-between">
              <span class="text-sm text-n-slate-11">{{
                t('VOICE.CENTER.VOICE_LINES')
              }}</span>
              <i class="i-lucide-phone text-n-brand" />
            </div>
            <p class="mt-3 text-3xl font-semibold text-n-slate-12">
              {{ voiceInboxes.length }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ t('VOICE.CENTER.WHATSAPP_CLOUD_CONFIGURED') }}
            </p>
          </article>

          <article class="rounded-xl border border-n-weak bg-n-surface-1 p-5">
            <div class="flex items-center justify-between">
              <span class="text-sm text-n-slate-11">{{
                t('VOICE.CENTER.RECEIVING_ACTIVE')
              }}</span>
              <i class="i-lucide-phone-incoming text-n-teal-10" />
            </div>
            <p class="mt-3 text-3xl font-semibold text-n-slate-12">
              {{ receivingInboxes }}
              <span class="text-base font-normal text-n-slate-10"
                >/ {{ voiceInboxes.length }}</span
              >
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ t('VOICE.CENTER.ENABLED_LINES_RECEIVE') }}
            </p>
          </article>

          <article class="rounded-xl border border-n-weak bg-n-surface-1 p-5">
            <div class="flex items-center justify-between">
              <span class="text-sm text-n-slate-11">{{
                t('VOICE.CENTER.AVAILABLE_AGENTS')
              }}</span>
              <i class="i-lucide-users-round text-n-brand" />
            </div>
            <p class="mt-3 text-3xl font-semibold text-n-slate-12">
              {{ onlineAgents }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{
                t('VOICE.CENTER.AGENT_COUNTS', {
                  busy: busyAgents,
                  eligible: allAgents.length,
                })
              }}
            </p>
          </article>

          <article class="rounded-xl border border-n-weak bg-n-surface-1 p-5">
            <div class="flex items-center justify-between">
              <span class="text-sm text-n-slate-11">{{
                t('VOICE.CENTER.ACTIVE_CALLS')
              }}</span>
              <i class="i-lucide-phone-call text-n-brand" />
            </div>
            <p class="mt-3 text-3xl font-semibold text-n-slate-12">
              {{ liveMonitoredCalls.length }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ t('VOICE.CENTER.ACCESSIBLE_INBOXES_VIEW') }}
            </p>
          </article>
        </section>

        <section
          class="mb-7 rounded-xl border border-n-weak bg-n-surface-1 p-5"
        >
          <div class="flex flex-wrap items-end justify-between gap-4 mb-4">
            <div>
              <h2 class="font-semibold text-n-slate-12">
                {{ t('VOICE.CENTER.REALTIME_MONITORING') }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-11">
                {{
                  t('VOICE.CENTER.MONITORING_SUMMARY', {
                    queued: queuedCalls.length,
                    ai: aiCalls.length,
                    human: humanCalls.length,
                  })
                }}
                <span v-if="staleCalls.length" class="text-n-amber-11">
                  ·
                  {{
                    t('VOICE.CENTER.SYNC_PENDING_COUNT', {
                      count: staleCalls.length,
                    })
                  }}
                </span>
              </p>
            </div>
            <span class="text-xs text-n-slate-10">
              {{
                t('VOICE.CENTER.ACTIVE_AND_PENDING_COUNT', {
                  active: liveMonitoredCalls.length,
                  pending: staleCalls.length,
                })
              }}
            </span>
          </div>
          <div
            v-if="monitoredCalls.length"
            class="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3"
          >
            <article
              v-for="call in monitoredCalls"
              :key="call.id"
              class="flex items-start gap-3 rounded-lg border border-n-weak bg-n-surface-2 p-3"
            >
              <div
                class="grid place-items-center size-10 shrink-0 rounded-full bg-n-brand/10 text-n-brand"
              >
                <i
                  :class="
                    call.state === 'stale'
                      ? 'i-lucide-triangle-alert text-n-amber-10'
                      : 'i-lucide-phone'
                  "
                />
              </div>
              <div class="min-w-0 flex-1">
                <p class="truncate font-medium text-n-slate-12">
                  {{
                    call.caller?.name ||
                    call.caller?.phone ||
                    t('VOICE.CENTER.CONTACT')
                  }}
                </p>
                <p class="mt-0.5 truncate text-xs text-n-slate-10">
                  {{ call.inbox_name }} · {{ callDestinationLabel(call) }}
                </p>
                <p class="mt-1 text-xs text-n-slate-11">
                  {{
                    call.state === 'stale'
                      ? t('VOICE.CENTER.STALE_CALL')
                      : call.state === 'transferring'
                        ? t('VOICE.CENTER.TRANSFERRING', {
                            destination:
                              call.destination ||
                              t('VOICE.CENTER.GENERAL_QUEUE'),
                          })
                        : call.state === 'queued'
                          ? t('VOICE.CENTER.WAITING_FOR_AGENT')
                          : call.state === 'connecting'
                            ? t('VOICE.CENTER.AGENT_CONNECTING')
                            : call.state === 'agent'
                              ? t('VOICE.CENTER.WITH_AGENT', {
                                  agent:
                                    call.agent_name || t('VOICE.CENTER.AGENT'),
                                })
                              : call.state === 'lumerai'
                                ? t('VOICE.CENTER.WITH_LUMERAI')
                                : call.status
                  }}
                  · {{ Math.floor(call.elapsed_seconds / 60) }}:{{
                    String(call.elapsed_seconds % 60).padStart(2, '0')
                  }}
                </p>
                <Button
                  v-if="call.state === 'stale' && call.can_terminate"
                  class="mt-2"
                  :label="t('VOICE.CENTER.FORCE_TERMINATION')"
                  icon="i-lucide-phone-off"
                  variant="faded"
                  color="ruby"
                  size="sm"
                  @click="requestForcedTermination(call)"
                />
                <Button
                  v-else-if="call.can_terminate"
                  class="mt-2"
                  :label="t('VOICE.CENTER.END_CALL')"
                  icon="i-lucide-phone-off"
                  variant="faded"
                  color="ruby"
                  size="sm"
                  @click="requestForcedTermination(call)"
                />
                <div
                  v-if="call.state === 'agent' && call.can_transfer"
                  class="mt-2 flex flex-wrap items-center gap-2"
                >
                  <label class="sr-only" :for="`transfer-${call.id}`">
                    {{ t('VOICE.CENTER.TRANSFER_DESTINATION') }}
                  </label>
                  <select
                    :id="`transfer-${call.id}`"
                    v-model="transferTargets[call.id]"
                    class="form-input min-w-40 flex-1"
                    @change="resetTransferCustomerRequest(call.id)"
                  >
                    <option value="">
                      {{ t('VOICE.CENTER.SELECT_TRANSFER_DESTINATION') }}
                    </option>
                    <option value="queue">
                      {{ t('VOICE.CENTER.GENERAL_QUEUE') }}
                    </option>
                    <option
                      v-for="team in call.transfer_teams || []"
                      :key="`transfer-team-${team.id}`"
                      :value="`Team:${team.id}`"
                    >
                      {{
                        t('VOICE.CENTER.TEAM_DESTINATION', { name: team.name })
                      }}
                    </option>
                  </select>
                  <label
                    class="flex w-full items-start gap-2 text-xs text-n-slate-11"
                  >
                    <input
                      v-model="transferCustomerRequested[call.id]"
                      type="checkbox"
                      class="mt-0.5"
                      :disabled="transferringCallId === call.id"
                    />
                    <span>{{
                      t('VOICE.CENTER.TRANSFER_CUSTOMER_REQUESTED')
                    }}</span>
                  </label>
                  <Button
                    :label="t('VOICE.CENTER.TRANSFER_CALL')"
                    icon="i-lucide-phone-forwarded"
                    variant="outline"
                    size="sm"
                    :is-loading="transferringCallId === call.id"
                    :disabled="
                      !transferTargets[call.id] ||
                      !transferCustomerRequested[call.id] ||
                      transferringCallId === call.id
                    "
                    @click="transferCall(call)"
                  />
                </div>
              </div>
            </article>
          </div>
          <p
            v-else
            class="rounded-lg border border-dashed border-n-weak p-6 text-center text-sm text-n-slate-10"
          >
            {{ t('VOICE.CENTER.NO_ACTIVE_CALLS_ACCESSIBLE') }}
          </p>
        </section>

        <section
          v-if="callMetrics"
          class="mb-7 rounded-xl border border-n-weak bg-n-surface-1 p-5"
        >
          <div class="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 class="font-semibold text-n-slate-12">
                {{ t('VOICE.CENTER.QUALITY_LAST_24_HOURS') }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('VOICE.CENTER.CLOSED_CALLS_SUMMARY') }}
              </p>
            </div>
            <span class="text-xs text-n-slate-10">
              {{
                t('VOICE.CENTER.CLOSED_CALLS_COUNT', {
                  count: callMetrics.total,
                })
              }}
            </span>
          </div>
          <div class="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-3">
              <p class="text-xs text-n-slate-10">
                {{ t('VOICE.CENTER.COMPLETED') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-teal-11">
                {{ callMetrics.completed }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-3">
              <p class="text-xs text-n-slate-10">
                {{ t('VOICE.CENTER.NO_ANSWER') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{ callMetrics.unanswered }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-3">
              <p class="text-xs text-n-slate-10">
                {{ t('VOICE.CENTER.FAILED_REJECTED') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-amber-11">
                {{ callMetrics.failed }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-3">
              <p class="text-xs text-n-slate-10">
                {{ t('VOICE.CENTER.ANSWERED_BY_AI') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{ callMetrics.answered_by_ai }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-3">
              <p class="text-xs text-n-slate-10">
                {{ t('VOICE.CENTER.AVERAGE_DURATION') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{
                  callMetrics.average_duration_seconds == null
                    ? '—'
                    : `${Math.floor(callMetrics.average_duration_seconds / 60)}:${String(callMetrics.average_duration_seconds % 60).padStart(2, '0')}`
                }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-3">
              <p class="text-xs text-n-slate-10">
                {{ t('VOICE.CENTER.AVERAGE_WAIT') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{
                  callMetrics.average_wait_seconds == null
                    ? '—'
                    : `${Math.floor(callMetrics.average_wait_seconds / 60)}:${String(callMetrics.average_wait_seconds % 60).padStart(2, '0')}`
                }}
              </p>
            </div>
          </div>
          <div class="mt-5">
            <h3 class="mb-3 text-sm font-medium text-n-slate-12">
              {{ t('VOICE.CENTER.EVENT_HISTORY') }}
            </h3>
            <div
              v-if="recentCalls.length"
              class="grid grid-cols-1 gap-2 lg:grid-cols-2"
            >
              <article
                v-for="call in recentCalls"
                :key="`history-${call.id}`"
                class="rounded-lg border border-n-weak bg-n-alpha-1 p-3"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <p class="truncate text-sm font-medium text-n-slate-12">
                      {{
                        call.caller?.name ||
                        call.caller?.phone ||
                        t('VOICE.CENTER.CONTACT')
                      }}
                    </p>
                    <p class="mt-1 text-xs text-n-slate-10">
                      {{ call.inbox_name }} · {{ callDestinationLabel(call) }} ·
                      {{
                        call.duration_seconds == null
                          ? t('VOICE.CENTER.DURATION_UNAVAILABLE')
                          : `${Math.floor(call.duration_seconds / 60)}:${String(call.duration_seconds % 60).padStart(2, '0')}`
                      }}
                    </p>
                  </div>
                  <span
                    class="shrink-0 rounded-full bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
                  >
                    {{ callStatusLabel(call.status) }}
                  </span>
                </div>
                <p v-if="call.end_reason" class="mt-2 text-xs text-n-slate-10">
                  {{
                    t('VOICE.CENTER.REASON', {
                      reason: call.end_reason.replaceAll('_', ' '),
                    })
                  }}
                </p>
                <p
                  v-if="call.call_summary"
                  class="mt-2 text-xs text-n-slate-11"
                >
                  {{
                    t('VOICE.CENTER.SUMMARY', { summary: call.call_summary })
                  }}
                </p>
                <p
                  v-if="call.voice_quality"
                  class="mt-2 text-xs text-n-slate-10"
                >
                  {{ t('VOICE.CENTER.AI_AUDIO') }}
                  {{ call.voice_quality.assistant_audio_responses || 0 }}
                  {{
                    t('VOICE.CENTER.AUDIO_RESPONSE_COUNT', {
                      count: call.voice_quality.assistant_audio_responses || 0,
                    })
                  }}
                  · {{ t('VOICE.CENTER.AVERAGE_FIRST_AUDIO') }}
                  {{
                    averageFirstAudioLatency(call.voice_quality) == null
                      ? t('VOICE.CENTER.NO_SAMPLE')
                      : `${averageFirstAudioLatency(call.voice_quality)} ms`
                  }}
                  ·
                  {{
                    t('VOICE.CENTER.TRANSCRIPTION_FAILURE_COUNT', {
                      count: call.voice_quality.transcription_failures || 0,
                    })
                  }}
                  ·
                  {{
                    t('VOICE.CENTER.REALTIME_ERROR_COUNT', {
                      count: call.voice_quality.realtime_errors || 0,
                    })
                  }}
                  <template v-if="call.voice_quality.samples">
                    ·
                    {{
                      t('VOICE.CENTER.WEBRTC_METRICS', {
                        rtt: call.voice_quality.rtt_ms ?? '—',
                        jitter: call.voice_quality.jitter_ms ?? '—',
                        lost: call.voice_quality.packets_lost || 0,
                        samples: call.voice_quality.samples,
                      })
                    }}
                  </template>
                </p>
                <details v-if="call.transcript_excerpt" class="mt-2">
                  <summary class="cursor-pointer text-xs text-n-brand">
                    {{ t('VOICE.CENTER.VIEW_TRANSCRIPT') }}
                  </summary>
                  <pre
                    class="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-n-surface-2 p-3 text-xs text-n-slate-11"
                    >{{ call.transcript_excerpt }}</pre
                  >
                </details>
                <details v-if="call.lifecycle_events?.length" class="mt-2">
                  <summary class="cursor-pointer text-xs text-n-brand">
                    {{
                      t('VOICE.CENTER.VIEW_CALL_EVENTS', {
                        count: call.lifecycle_events.length,
                      })
                    }}
                  </summary>
                  <ol class="mt-2 space-y-1 border-l border-n-weak pl-3">
                    <li
                      v-for="event in [...call.lifecycle_events].reverse()"
                      :key="event.id"
                      class="text-xs text-n-slate-11"
                    >
                      <span class="font-medium text-n-slate-12">{{
                        lifecycleEventLabel(event.event)
                      }}</span>
                      <span v-if="event.details?.destination">
                        · {{ event.details.destination }}</span
                      >
                      <span v-if="event.details?.team_name">
                        · {{ event.details.team_name }}</span
                      >
                      <time
                        v-if="event.occurred_at"
                        class="ml-1 text-n-slate-10"
                      >
                        {{
                          new Date(event.occurred_at).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        }}
                      </time>
                    </li>
                  </ol>
                </details>
              </article>
            </div>
            <p
              v-else
              class="rounded-lg border border-dashed border-n-weak p-4 text-center text-sm text-n-slate-10"
            >
              {{ t('VOICE.CENTER.NO_CLOSED_CALLS') }}
            </p>
          </div>
        </section>

        <section
          v-if="activeWhatsappCalls.length"
          class="mb-7 rounded-xl border border-n-brand/30 bg-n-brand/5 p-5"
        >
          <div class="flex items-center justify-between gap-4 mb-4">
            <div>
              <h2 class="font-semibold text-n-slate-12">
                {{ t('VOICE.CENTER.SESSION_ALERTS') }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('VOICE.CENTER.SESSION_CALLS_DESCRIPTION') }}
              </p>
            </div>
            <span
              class="rounded-full px-2.5 py-1 text-xs bg-n-brand/10 text-n-brand"
              >{{
                t('VOICE.CENTER.ACTIVE_COUNT', {
                  count: activeWhatsappCalls.length,
                })
              }}</span
            >
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            <article
              v-for="call in activeWhatsappCalls"
              :key="call.callSid"
              class="flex items-center gap-3 rounded-lg border border-n-weak bg-n-surface-1 p-3"
            >
              <div
                class="grid place-items-center size-10 rounded-full bg-n-brand/10 text-n-brand"
              >
                <i class="i-lucide-phone-incoming" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="font-medium text-n-slate-12 truncate">
                  {{
                    call.caller?.name ||
                    call.caller?.phone ||
                    t('VOICE.CENTER.CONTACT')
                  }}
                </p>
                <p class="text-xs text-n-slate-10">
                  {{
                    t('VOICE.CENTER.CALL_STATE_INBOX', {
                      state: callStateLabel(call),
                      inbox: call.inboxId,
                    })
                  }}
                </p>
              </div>
            </article>
          </div>
        </section>

        <section class="mb-7">
          <div class="flex flex-wrap items-end justify-between gap-4 mb-4">
            <div>
              <h2 class="text-lg font-semibold text-n-slate-12">
                {{ t('VOICE.CENTER.CALL_OPERATIONS') }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('VOICE.CENTER.OPERATIONS_DESCRIPTION') }}
              </p>
            </div>
            <div class="flex items-center gap-3">
              <div
                role="tablist"
                :aria-label="t('VOICE.CENTER.OPERATIONS_VIEW')"
                class="inline-flex rounded-lg border border-n-weak bg-n-surface-1 p-1"
              >
                <button
                  type="button"
                  role="tab"
                  :aria-selected="operationsView === 'agents'"
                  class="rounded-md px-3 py-2 text-sm transition-colors"
                  :class="
                    operationsView === 'agents'
                      ? 'bg-n-brand/10 text-n-brand'
                      : 'text-n-slate-11 hover:bg-n-alpha-2'
                  "
                  @click="operationsView = 'agents'"
                >
                  <i class="i-lucide-users-round mr-1.5" />{{
                    t('VOICE.CENTER.AGENTS_TAB')
                  }}
                </button>
                <button
                  type="button"
                  role="tab"
                  :aria-selected="operationsView === 'queues'"
                  class="rounded-md px-3 py-2 text-sm transition-colors"
                  :class="
                    operationsView === 'queues'
                      ? 'bg-n-brand/10 text-n-brand'
                      : 'text-n-slate-11 hover:bg-n-alpha-2'
                  "
                  @click="operationsView = 'queues'"
                >
                  <i class="i-lucide-list-ordered mr-1.5" />{{
                    t('VOICE.CENTER.QUEUES_TAB')
                  }}
                </button>
              </div>
              <RouterLink
                v-if="canManageCallSettings"
                :to="{
                  name: 'voice_queues_settings',
                  params: { accountId: route.params.accountId },
                }"
                class="text-sm text-n-brand hover:underline"
                >{{ t('VOICE.CENTER.CONFIGURE_QUEUES') }}</RouterLink
              >
            </div>
          </div>

          <div
            v-if="operationsView === 'agents' && voiceInboxes.length"
            class="grid grid-cols-1 gap-4"
          >
            <article
              v-for="inbox in voiceInboxes"
              :key="inbox.id"
              class="rounded-xl border border-n-weak bg-n-surface-1 overflow-hidden"
            >
              <div
                class="flex items-start justify-between gap-4 p-5 border-b border-n-weak"
              >
                <div class="flex items-center gap-3 min-w-0">
                  <img
                    v-if="inbox.avatar_url"
                    :src="inbox.avatar_url"
                    alt=""
                    class="size-10 rounded-full"
                  />
                  <div
                    v-else
                    class="grid place-items-center size-10 rounded-full bg-n-brand/10 text-n-brand font-semibold"
                  >
                    {{ inbox.name?.charAt(0) || 'W' }}
                  </div>
                  <div class="min-w-0">
                    <h3 class="font-semibold text-n-slate-12 truncate">
                      {{ inbox.name }}
                    </h3>
                    <p class="mt-1 text-xs text-n-slate-10">
                      {{ t('VOICE.CENTER.WHATSAPP_INBOX', { id: inbox.id }) }}
                    </p>
                  </div>
                </div>
                <span
                  class="shrink-0 rounded-full px-2.5 py-1 text-xs"
                  :class="
                    inbox.inbound_calls_enabled
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-amber-3 text-n-amber-11'
                  "
                >
                  {{
                    inbox.inbound_calls_enabled
                      ? t('VOICE.CENTER.RECEIVING')
                      : t('VOICE.CENTER.PAUSED')
                  }}
                </span>
              </div>

              <div class="p-5">
                <div class="flex items-center justify-between gap-4 mb-3">
                  <div>
                    <p class="text-sm font-medium text-n-slate-12">
                      {{
                        inbox.call_queue_config?.strategy === 'ring_all'
                          ? t('VOICE.STRATEGIES.RING_ALL')
                          : t('VOICE.STRATEGIES.ASSIGNED_FIRST')
                      }}
                    </p>
                    <p class="mt-1 text-xs text-n-slate-10">
                      {{
                        t('VOICE.CENTER.ELIGIBLE_AVAILABLE_AGENTS', {
                          eligible: eligibleAgents(inbox).length,
                          available: eligibleAgents(inbox).filter(
                            agent => agent.availability_status === 'online'
                          ).length,
                        })
                      }}
                    </p>
                  </div>
                  <RouterLink
                    v-if="canManageCallSettings"
                    :to="{
                      name: 'voice_queues_settings',
                      params: { accountId: route.params.accountId },
                    }"
                    class="grid place-items-center size-9 rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-2"
                    :aria-label="
                      t('VOICE.CENTER.CONFIGURE_QUEUE', { name: inbox.name })
                    "
                  >
                    <i class="i-lucide-settings-2" />
                  </RouterLink>
                </div>

                <div
                  v-if="eligibleAgents(inbox).length"
                  class="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-2"
                >
                  <span
                    v-for="agent in eligibleAgents(inbox)"
                    :key="agent.id"
                    class="inline-flex min-w-0 items-center gap-2 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2"
                  >
                    <span
                      class="size-2 rounded-full"
                      :class="
                        agent.availability_status === 'online'
                          ? 'bg-n-teal-9'
                          : agent.availability_status === 'busy'
                            ? 'bg-n-amber-9'
                            : 'bg-n-slate-8'
                      "
                    />
                    <span
                      class="min-w-0 flex-1 truncate text-sm text-n-slate-12"
                      >{{ agent.name }}</span
                    >
                    <span
                      class="text-[11px] rounded-full px-2 py-0.5"
                      :class="agentStatusClass(agent.availability_status)"
                      >{{ agentStatusLabel(agent.availability_status) }}</span
                    >
                  </span>
                </div>
                <p v-else class="text-sm text-n-amber-11">
                  {{ t('VOICE.CENTER.NO_ELIGIBLE_AGENTS') }}
                </p>
              </div>
            </article>
          </div>

          <div
            v-else-if="operationsView === 'queues' && voiceInboxes.length"
            class="grid grid-cols-1 gap-4"
          >
            <article
              v-for="inbox in voiceInboxes"
              :key="inbox.id"
              class="overflow-hidden rounded-xl border border-n-weak bg-n-surface-1"
            >
              <div
                class="flex flex-wrap items-center justify-between gap-3 border-b border-n-weak p-5"
              >
                <div class="flex min-w-0 items-center gap-3">
                  <div
                    class="grid size-10 shrink-0 place-items-center rounded-full bg-n-brand/10 font-semibold text-n-brand"
                  >
                    {{ inbox.name?.charAt(0) || 'W' }}
                  </div>
                  <div class="min-w-0">
                    <h3 class="truncate font-semibold text-n-slate-12">
                      {{ inbox.name }}
                    </h3>
                    <p class="text-xs text-n-slate-10">
                      {{ t('VOICE.CENTER.WHATSAPP_CLOUD') }} ·
                      {{
                        inbox.call_queue_config?.strategy === 'ring_all'
                          ? t('VOICE.STRATEGIES.RING_ALL')
                          : t('VOICE.STRATEGIES.ROUND_ROBIN')
                      }}
                    </p>
                  </div>
                </div>
                <span
                  class="rounded-full px-2.5 py-1 text-xs"
                  :class="
                    inbox.inbound_calls_enabled
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-amber-3 text-n-amber-11'
                  "
                >
                  {{
                    inbox.inbound_calls_enabled
                      ? t('VOICE.CENTER.QUEUE_RECEIVING')
                      : t('VOICE.CENTER.RECEIVING_PAUSED')
                  }}
                </span>
              </div>

              <div class="grid grid-cols-2 gap-3 p-5 md:grid-cols-4">
                <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-3">
                  <p class="text-xs text-n-slate-10">
                    {{ t('VOICE.CENTER.WAITING_TRANSFER') }}
                  </p>
                  <p class="mt-1 text-2xl font-semibold text-n-slate-12">
                    {{ inboxCallCount(inbox.id, ['queued', 'connecting']) }}
                  </p>
                </div>
                <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-3">
                  <p class="text-xs text-n-slate-10">
                    {{ t('VOICE.CENTER.WITH_AGENT_LABEL') }}
                  </p>
                  <p class="mt-1 text-2xl font-semibold text-n-teal-11">
                    {{ inboxCallCount(inbox.id, ['agent']) }}
                  </p>
                </div>
                <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-3">
                  <p class="text-xs text-n-slate-10">
                    {{ t('VOICE.CENTER.WITH_LUMERAI_LABEL') }}
                  </p>
                  <p class="mt-1 text-2xl font-semibold text-n-slate-12">
                    {{ inboxCallCount(inbox.id, ['lumerai']) }}
                  </p>
                </div>
                <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-3">
                  <p class="text-xs text-n-slate-10">
                    {{ t('VOICE.CENTER.SYNC_PENDING') }}
                  </p>
                  <p class="mt-1 text-2xl font-semibold text-n-amber-11">
                    {{ inboxCallCount(inbox.id, ['stale']) }}
                  </p>
                </div>
              </div>

              <div class="border-t border-n-weak px-5 pb-5">
                <h4 class="mb-3 text-sm font-medium text-n-slate-12">
                  {{ t('VOICE.CENTER.CALLS_IN_QUEUE') }}
                </h4>
                <div
                  v-if="inboxCalls(inbox.id).length"
                  class="grid grid-cols-1 gap-2 lg:grid-cols-2"
                >
                  <div
                    v-for="call in inboxCalls(inbox.id)"
                    :key="call.id"
                    class="flex min-w-0 flex-wrap items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2.5"
                  >
                    <div class="min-w-0">
                      <p class="truncate text-sm font-medium text-n-slate-12">
                        {{
                          call.caller?.name ||
                          call.caller?.phone ||
                          t('VOICE.CENTER.CONTACT')
                        }}
                      </p>
                      <p class="mt-0.5 text-xs text-n-slate-10">
                        {{ callDestinationLabel(call) }} ·
                        {{ Math.floor(call.elapsed_seconds / 60) }}:{{
                          String(call.elapsed_seconds % 60).padStart(2, '0')
                        }}
                        <span v-if="call.wait_seconds != null">
                          ·
                          {{
                            t('VOICE.CENTER.WAIT_TIME', {
                              time: `${Math.floor(call.wait_seconds / 60)}:${String(call.wait_seconds % 60).padStart(2, '0')}`,
                            })
                          }}
                        </span>
                        <span v-if="routingStageLabel(call)">
                          · {{ routingStageLabel(call) }}
                        </span>
                      </p>
                    </div>
                    <span
                      class="shrink-0 rounded-full px-2 py-1 text-xs"
                      :class="monitoredCallTone(call.state)"
                    >
                      {{ monitoredCallLabel(call) }}
                    </span>
                    <div
                      v-if="
                        call.can_reroute &&
                        (call.state === 'queued' ||
                          call.state === 'transferring')
                      "
                      class="flex w-full flex-wrap items-center gap-2 border-t border-n-weak pt-2"
                    >
                      <label class="sr-only" :for="`reroute-${call.id}`">
                        {{ t('VOICE.CENTER.REROUTE_CALL') }}
                      </label>
                      <select
                        :id="`reroute-${call.id}`"
                        v-model="rerouteTargets[call.id]"
                        class="form-input min-w-48 flex-1"
                      >
                        <option value="queue">
                          {{ t('VOICE.CENTER.GENERAL_QUEUE') }}
                        </option>
                        <option
                          v-for="agent in agentsByInbox[inbox.id] || []"
                          :key="`agent-${agent.id}`"
                          :value="`User:${agent.id}`"
                        >
                          {{
                            t('VOICE.CENTER.AGENT_DESTINATION', {
                              name: agent.name,
                            })
                          }}
                        </option>
                        <option
                          v-for="team in teams"
                          :key="`team-${team.id}`"
                          :value="`Team:${team.id}`"
                        >
                          {{
                            t('VOICE.CENTER.TEAM_DESTINATION', {
                              name: team.name,
                            })
                          }}
                        </option>
                      </select>
                      <Button
                        :label="t('VOICE.CENTER.REROUTE')"
                        icon="i-lucide-send"
                        variant="outline"
                        size="sm"
                        :is-loading="reroutingCallId === call.id"
                        :disabled="reroutingCallId === call.id"
                        @click="rerouteCall(call)"
                      />
                    </div>
                  </div>
                </div>
                <p
                  v-else
                  class="rounded-lg border border-dashed border-n-weak p-4 text-center text-sm text-n-slate-10"
                >
                  {{ t('VOICE.CENTER.NO_CALLS_IN_QUEUE') }}
                </p>
              </div>
            </article>
          </div>

          <div
            v-else
            class="rounded-xl border border-dashed border-n-weak bg-n-surface-1 p-10 text-center"
          >
            <i class="i-lucide-phone-off text-3xl text-n-slate-9" />
            <h3 class="mt-3 font-medium text-n-slate-12">
              {{ t('VOICE.CENTER.NO_ACTIVE_LINES') }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11 max-w-xl mx-auto">
              {{ t('VOICE.CENTER.ACTIVATE_LINES_HELP') }}
            </p>
            <RouterLink
              :to="{
                name: 'settings_inbox_list',
                params: { accountId: route.params.accountId },
              }"
              class="inline-flex mt-4 text-sm text-n-brand hover:underline"
              >{{ t('VOICE.CENTER.OPEN_INBOX_SETTINGS') }}</RouterLink
            >
          </div>
        </section>

        <p class="text-xs text-n-slate-10 border-t border-n-weak pt-4">
          {{ t('VOICE.CENTER.STATUS_DISCLAIMER') }}
        </p>
      </template>
    </div>
    <Dialog
      ref="terminationDialog"
      type="alert"
      :title="
        selectedCallToTerminate?.state === 'stale'
          ? t('VOICE.CENTER.CONFIRM_FORCE_TERMINATION')
          : t('VOICE.CENTER.CONFIRM_END_CALL')
      "
      :description="t('VOICE.CENTER.TERMINATION_CONFIRMATION')"
      :confirm-button-label="t('VOICE.CENTER.END_CALL')"
      :is-loading="isTerminatingCall"
      @confirm="confirmForcedTermination"
    />
  </main>
</template>
